<template>
  <div
    v-tooltip.top="
      !isAssistantConfigured
        ? 'The AI model is not configured. Please click to configure it in settings.'
        : null
    "
    class="relative group"
  >
    <button
      :disabled="isLoading"
      class="rounded-lg w-8 h-8 flex items-center justify-center bg-purple-600 text-white overflow-visible"
      @click="handleClick"
    >
      <span
        class="absolute top-1/2 right-full -translate-y-1/2 -mr-2 bg-purple-600 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 ease-in-out whitespace-nowrap z-50"
      >
        AI extraction
      </span>

      <SparklesIcon
        class="w-5 h-5 fill-white"
        :class="{
          'animate-spin': isLoading,
        }"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { ref } from "vue";
import { ReviewKey } from "../../../use-review";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import useSnackbar from "@app/composables/use-snackbar";
import useAiAssistant from "@app/composables/useAiAssistant";
import { SparklesIcon } from "@heroicons/vue/24/outline";
import { useRouter } from "vue-router";
const props = withDefaults(
  defineProps<{
    study: ReviewItem;
    attributeStructure: AttributeStructure;
    query: string;
  }>(),
  {
    query: "",
  },
);

const snackbar = useSnackbar();
const { isConfigured: isAssistantConfigured } = useAiAssistant();

const isGeneratingAnswer = defineModel<boolean>("isGeneratingAnswer", {
  default: false,
});

const emit = defineEmits<{
  aiCompleted: [];
}>();

const review = injectStrict(ReviewKey);
const isLoading = ref(false);
const router = useRouter();

async function generateAnswer() {
  const attributeId = props.attributeStructure.id;
  if (!attributeId) {
    return;
  }
  isLoading.value = true;
  isGeneratingAnswer.value = true;
  try {
    await review.answerStudyQuestion(props.study.id, attributeId);
    snackbar.success("Success");
    emit("aiCompleted");
  } catch (_) {
    snackbar.error("The AI model timed out. Please try again.");
  } finally {
    isLoading.value = false;
    isGeneratingAnswer.value = false;
  }
}

function handleClick() {
  if (isAssistantConfigured.value) {
    generateAnswer();
  } else {
    router.push("/settings");
  }
}
</script>
